import React, { useState } from 'react';
import {
    addBlogPost,
    updateBlogPost,
    deleteBlogPost
} from '../../services/dataService';

const BlogSection = ({ siteData, setSiteData, saving, setSaving, showSaveStatus }) => {
    const [editingPosts, setEditingPosts] = useState({});
    const [savingStates, setSavingStates] = useState({});

    const handleLocalUpdate = (postId, field, value) => {
        setEditingPosts(prev => ({
            ...prev,
            [postId]: {
                ...(prev[postId] || siteData.blog.find(p => p.id === postId)),
                [field]: value,
                hasChanges: true
            }
        }));
    };

    const handleSavePost = async (postId) => {
        try {
            setSavingStates(prev => ({ ...prev, [postId]: true }));
            const updatedPost = editingPosts[postId];

            await updateBlogPost(postId, updatedPost);

            const updatedBlog = siteData.blog.map(post =>
                post.id === postId ? { ...updatedPost, id: postId } : post
            );
            setSiteData(prev => ({ ...prev, blog: updatedBlog }));

            // Clear editing state
            setEditingPosts(prev => {
                const newState = { ...prev };
                delete newState[postId];
                return newState;
            });

            showSaveStatus('Blog post updated successfully!');
        } catch (error) {
            showSaveStatus('Failed to update blog post', true);
        } finally {
            setSavingStates(prev => ({ ...prev, [postId]: false }));
        }
    };

    const handleAddBlogPost = async () => {
        try {
            setSaving(true);
            const newPost = {
                title: 'New Blog Post',
                excerpt: 'Post excerpt',
                date: new Date().toISOString().split('T')[0],
                category: 'Mental Health'
            };

            const postId = await addBlogPost(newPost);
            const postWithId = { ...newPost, id: postId };

            setSiteData(prev => ({
                ...prev,
                blog: [postWithId, ...prev.blog]
            }));
            showSaveStatus('Blog post added successfully!');
        } catch (error) {
            showSaveStatus('Failed to add blog post', true);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteBlogPost = async (postId) => {
        if (!window.confirm('Are you sure you want to delete this blog post?')) {
            return;
        }

        try {
            setSaving(true);
            await deleteBlogPost(postId);

            const updatedBlog = siteData.blog.filter(post => post.id !== postId);
            setSiteData(prev => ({ ...prev, blog: updatedBlog }));

            // Clear editing state if exists
            setEditingPosts(prev => {
                const newState = { ...prev };
                delete newState[postId];
                return newState;
            });

            showSaveStatus('Blog post deleted successfully!');
        } catch (error) {
            showSaveStatus('Failed to delete blog post', true);
        } finally {
            setSaving(false);
        }
    };

    const getPostData = (postId) => {
        return editingPosts[postId] || siteData.blog.find(p => p.id === postId);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-stone-800">Blog Posts</h3>
                <button
                    onClick={handleAddBlogPost}
                    disabled={saving}
                    className="bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50"
                >
                    {saving ? 'Adding...' : 'Add Blog Post'}
                </button>
            </div>

            <div className="space-y-4">
                {siteData.blog.map((post) => {
                    const currentPost = getPostData(post.id);
                    const hasChanges = editingPosts[post.id]?.hasChanges;
                    const isSaving = savingStates[post.id];

                    return (
                        <div key={post.id} className="bg-stone-50 p-4 rounded-lg">
                            <div className="flex justify-between items-start mb-4">
                                <h4 className="font-medium text-stone-800">Post: {currentPost.title}</h4>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleSavePost(post.id)}
                                        disabled={!hasChanges || isSaving}
                                        className={`px-4 py-1 rounded transition-colors text-sm ${hasChanges && !isSaving
                                            ? 'bg-amber-500 text-white hover:bg-amber-600'
                                            : 'bg-stone-300 text-stone-500 cursor-not-allowed'
                                            }`}
                                    >
                                        {isSaving ? 'Saving...' : hasChanges ? 'Save' : 'Saved'}
                                    </button>
                                    <button
                                        onClick={() => handleDeleteBlogPost(post.id)}
                                        disabled={saving}
                                        className="text-red-500 hover:text-red-700 disabled:opacity-50 px-2"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1">Title</label>
                                    <input
                                        type="text"
                                        value={currentPost.title}
                                        onChange={(e) => handleLocalUpdate(post.id, 'title', e.target.value)}
                                        className="w-full px-3 py-2 border border-stone-300 rounded focus:ring-1 focus:ring-amber-400"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1">Category</label>
                                    <input
                                        type="text"
                                        value={currentPost.category}
                                        onChange={(e) => handleLocalUpdate(post.id, 'category', e.target.value)}
                                        className="w-full px-3 py-2 border border-stone-300 rounded focus:ring-1 focus:ring-amber-400"
                                    />
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-stone-700 mb-1">Date</label>
                                <input
                                    type="date"
                                    value={currentPost.date}
                                    onChange={(e) => handleLocalUpdate(post.id, 'date', e.target.value)}
                                    className="w-full px-3 py-2 border border-stone-300 rounded focus:ring-1 focus:ring-amber-400"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-stone-700 mb-1">Excerpt</label>
                                <textarea
                                    value={currentPost.excerpt}
                                    onChange={(e) => handleLocalUpdate(post.id, 'excerpt', e.target.value)}
                                    className="w-full px-3 py-2 border border-stone-300 rounded focus:ring-1 focus:ring-amber-400"
                                    rows="2"
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default BlogSection;